/**
 * Message type — the kind of copy a message carries.
 *
 * Modeled as an enum column for now because the full set of types and how they
 * are used is not yet settled. Candidate to migrate to a `message_type`
 * classification axis once it stabilizes (values-as-INSERTs, browsable through
 * the classification core) — see the add-validation-asset-generation design.
 *
 * Message hierarchy is driven per campaign / per-piece need — there are no
 * strongly generalized rules for the creative copy, but mapping the individual
 * elements works well in practice. LEGAL text, however, does carry a fixed,
 * non-negotiable hierarchy and order:
 *   - AI_DISCLAIMER — fixed for a static asset; for video it must be shown for
 *     ≥60% of the asset's duration.
 *   - The legal block is composed as: COPYRIGHT on the top line + DISCLAIMER
 *     (legal text / ISI) on the line below + VEEVA_CODE. (Copyright is a
 *     relatively new requirement legal now asks for.)
 * (Reference: a static Numelvi banner carries all of these.)
 */
export enum MessageType {
	TAGLINE = 'tagline',
	COPY_IN = 'copy_in',
	COPY_OUT = 'copy_out',
	CTA = 'cta',
	STAGE = 'stage',
	TITLE = 'title',
	BODY = 'body',
	MAIN_CLAIM = 'main_claim',
	SECONDARY_CLAIM = 'secondary_claim',
	// Legal block (fixed order — see the hierarchy note above):
	COPYRIGHT = 'copyright',
	DISCLAIMER = 'disclaimer',
	// The rendered Veeva-code legal LINE carried by a message — distinct from the
	// `veeva_code` classification attribute axis on a work order (that stores the
	// code VALUE; this is the copy line shown in the piece).
	VEEVA_CODE = 'veeva_code',
	// Fixed on a static asset; on video must display for ≥60% of the duration.
	AI_DISCLAIMER = 'ai_disclaimer',
}

/** Lifecycle status of a message. */
export enum MessageStatus {
	DRAFT = 'draft',
	ACTIVE = 'active',
	ARCHIVED = 'archived',
}

/**
 * State of an in-flight AI translation job for a message row — orthogonal to
 * `MessageStatus` (publication lifecycle). Null/undefined means the row was
 * never machine-translated.
 */
export enum MessageTranslationStatus {
	PENDING = 'pending',
	TRANSLATING = 'translating',
	READY = 'ready',
	FAILED = 'failed',
}

/**
 * WHY a message became `active` — publishable copy.
 *
 * `status` records THAT a message may be used; this records who decided and on what basis.
 * The distinction matters for regulated copy: a machine translation published without a human
 * ever reading it is a materially different claim from one a reviewer approved, and an audit
 * that cannot tell them apart cannot answer the only question it exists to answer.
 */
export enum ActivationReason {
	/** A person reviewed the copy and approved it. */
	HUMAN_REVIEW = 'human_review',
	/**
	 * An automated flow published a finished machine translation with NO human review.
	 *
	 * Used by the prepare step, where nobody reviews. Recorded rather than left blank
	 * precisely because it is the weaker claim — a deliverable built on this copy can be
	 * identified later as never having been read by a person.
	 */
	AUTO_TRANSLATION = 'auto_translation',
	/** Seeded or imported as already-approved copy, e.g. an authored source line. */
	IMPORTED = 'imported',
}
